/**
 * Open Action Queue - Generic Widget
 * Works for insights from ANY workflow
 * Shows workflow source badge on each insight
 */

import { ListChecks, ArrowRight } from '@phosphor-icons/react'
import { Badge } from '@/components/ui/badge'
import type { DashboardInsight } from '../types'

interface OpenActionQueueWidgetProps {
  insights: DashboardInsight[]
  showWorkflowSource?: boolean
  limit?: number
}

export function OpenActionQueueWidget({
  insights,
  showWorkflowSource = true,
  limit = 10,
}: OpenActionQueueWidgetProps) {
  const actionableInsights = insights
    .filter((i) => i.status === 'new' || i.status === 'pending_review' || i.status === 'under_investigation')
    .sort((a, b) => {
      const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 }
      const diff = severityOrder[a.severity] - severityOrder[b.severity]
      return diff !== 0 ? diff : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })
    .slice(0, limit)

  // Count workflows represented
  const workflowSources = new Set(actionableInsights.map((i) => i.workflowSource))

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-[15px] border border-[#e6e6e6] bg-white">
      <div className="flex items-center gap-2 px-5 pb-4 pt-5">
        <h3 className="text-xl font-medium leading-[30px] tracking-[0.2px] text-[#1d1d1d]">Open Action Queue</h3>
        {showWorkflowSource && (
          <div className="flex items-center rounded-3xl bg-[#f5f5f5] px-2 py-1">
            <span className="text-[10px] font-bold leading-[15px] tracking-[0.1px] text-[#272727]">
              {workflowSources.size} workflow{workflowSources.size !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-auto">
        {actionableInsights.length === 0 ? (
          <div className="flex h-full items-center justify-center p-6 text-center">
            <div>
              <div className="mb-2 text-4xl">✓</div>
              <div className="font-semibold text-[#12833d]">All Caught Up!</div>
              <div className="text-sm text-[#535353]">No pending actions</div>
            </div>
          </div>
        ) : (
          <table className="w-full">
            <thead className="sticky top-0 bg-[#fafafa]">
              <tr className="text-left">
                <th className="px-4 pb-[17px] pt-4 text-sm font-normal tracking-[0.14px] text-[#1d1d1d]">Priority</th>
                <th className="px-4 pb-[17px] pt-4 text-sm font-normal tracking-[0.14px] text-[#1d1d1d]">Insight</th>
                {showWorkflowSource && <th className="px-4 pb-[17px] pt-4 text-sm font-normal tracking-[0.14px] text-[#1d1d1d]">Workflow</th>}
                <th className="px-4 pb-[17px] pt-4 text-sm font-normal tracking-[0.14px] text-[#1d1d1d]">Product</th>
                <th className="px-4 pb-[17px] pt-4 text-sm font-normal tracking-[0.14px] text-[#1d1d1d]">Lots</th>
                <th className="px-4 pb-[17px] pt-4 text-sm font-normal tracking-[0.14px] text-[#1d1d1d]">Status</th>
                <th className="px-4 pb-[17px] pt-4"></th>
              </tr>
            </thead>
            <tbody>
              {actionableInsights.map((insight) => (
                <tr
                  key={insight.insightId}
                  className="border-b border-[rgba(0,0,0,0.06)] bg-white transition-colors hover:bg-[#fafafa]"
                >
                  <td className="px-4 pb-[17px] pt-4">
                    <div className={`inline-flex items-center rounded-3xl px-2 py-1 ${
                        insight.severity === 'critical' || insight.severity === 'high'
                          ? 'bg-[#fee7e7] text-[#b41e2e]'
                          : 'bg-[#fff7ed] text-[#c83408]'
                      }`}
                    >
                      <span className="text-[10px] font-bold leading-[15px] tracking-[0.1px]">{insight.severity}</span>
                    </div>
                  </td>
                  <td className="px-4 pb-[17px] pt-4">
                    <div className="text-sm font-medium tracking-[0.14px] text-[#1d1d1d]">{insight.title}</div>
                  </td>
                  {showWorkflowSource && (
                    <td className="px-4 pb-[17px] pt-4">
                      <div className="inline-flex items-center rounded-3xl bg-[#e9f9ff] px-2 py-1">
                        <span className="text-[10px] font-bold leading-[15px] tracking-[0.1px] text-[#0091ff]">{insight.workflowSource}</span>
                      </div>
                    </td>
                  )}
                  <td className="px-4 pb-[17px] pt-4 text-xs tracking-[0.12px] text-[#727272]">{insight.affectedProducts[0]}</td>
                  <td className="px-4 pb-[17px] pt-4 text-center text-xs tracking-[0.12px] text-[#727272]">{insight.lotsImpacted}</td>
                  <td className="px-4 pb-[17px] pt-4">
                    <div className={`inline-flex items-center rounded-3xl px-2 py-1 ${
                        insight.status === 'new' ? 'bg-[#fee7e7] text-[#b41e2e]' :
                        insight.status === 'pending_review' ? 'bg-[#fff7ed] text-[#c83408]' :
                        'bg-[#dcfde8] text-[#12833d]'
                      }`}
                    >
                      <span className="text-[10px] font-bold leading-[15px] tracking-[0.1px]">{insight.status.replace(/_/g, ' ')}</span>
                    </div>
                  </td>
                  <td className="px-4 pb-[17px] pt-4">
                    <button className="rounded p-1 transition-colors hover:bg-[#f5f5f5]">
                      <ArrowRight className="h-[18px] w-[18px] text-[#727272]" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="border-t border-[#e6e6e6] bg-white px-5 py-3 text-xs tracking-[0.12px] text-[#727272]">
        Generic widget - shows insights from any workflow using standard schema
      </div>
    </div>
  )
}
